import { useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { preferencesAPI } from '@/services/api/preferences.api';

const WEEKDAY_INDEX: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

/**
 * Hora y día de la semana actuales en zona de Madrid. Usamos Madrid (no la
 * zona local del navegador) porque el cron de recordatorios interpreta
 * `reminderTime` como hora de Madrid; así el valor que registramos coincide
 * con cuándo se disparará realmente la notificación.
 */
function madridHourAndWeekday(now = new Date()): { hour: number; weekday: number } {
  const hour =
    Number(
      new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Madrid',
        hour: '2-digit',
        hour12: false,
      }).format(now),
    ) % 24;
  const weekdayName = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Madrid',
    weekday: 'long',
  }).format(now);
  return { hour, weekday: WEEKDAY_INDEX[weekdayName] ?? now.getDay() };
}

/**
 * La primera vez que un alumno entra en la app (cuando aún no tiene
 * `reminderTime` configurado), registra como hora de recordatorio de estudio
 * la hora a la que ha entrado, más el día de la semana. A partir de ahí el
 * alumno puede cambiarlo en Ajustes; este hook no vuelve a tocar nada porque
 * ya existirá `reminderTime`.
 *
 * `reminderWeekday` se guarda aunque todavía no se use en ningún cron — queda
 * disponible para futuros recordatorios basados en el día.
 */
export function useInitReminderTime(): void {
  const { user, updateUser } = useAuth();
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!user || user.role !== 'student' || attemptedRef.current) return;
    if (user.preferences?.reminderTime) return; // ya configurado: no es la primera vez
    attemptedRef.current = true;

    const { hour, weekday } = madridHourAndWeekday();
    const reminderTime = `${String(hour).padStart(2, '0')}:00`;

    void preferencesAPI
      .patch({ reminderTime, reminderWeekday: weekday })
      .then((merged) => updateUser({ preferences: merged }))
      .catch((err) => {
        attemptedRef.current = false; // permite reintento en la próxima carga
        console.error('Failed to register default reminder time:', err);
      });
  }, [user, updateUser]);
}
